import clsx from 'clsx';
import { Field, useField } from 'formik';
import React from 'react';

import { AutocompleteOption } from '@utils/interfaces';

import FieldError from './FieldError';
import FieldLabel from './FieldLabel';

interface RadioButtonGroupProps {
  label?: string;
  name: string;
  options: AutocompleteOption[];
  className?: string;
}

/**
 * @param {{name, label, options, ...}} props
 * options => [{label: "label 1", value: 1}, {label: "label 2", value: 2}, ...]
 * where label is displayed, and value is an optional value attached for the item.
 */

const RadioButtonGroup = (props: RadioButtonGroupProps) => {
  const { className, label, name, options } = props;
  const [field, meta, helpers] = useField(name);

  const handleOnChange = (value: string) => {
    helpers.setValue(value);
  };

  return (
    // Pass a width property to the parent div to set the width of the radio group
    <div className={className}>
      {label && <FieldLabel htmlFor={name}>{label}</FieldLabel>}
      {options.map((option) => (
        <div key={option.value} className="mb-8 flex items-center">
          <Field
            type="radio"
            name={name}
            id={option.value}
            value={option.value}
            checked={field.value === option.value}
            onChange={() => handleOnChange(option.value)}
            className="hidden"
          />
          <label
            htmlFor={option.value}
            className="flex cursor-pointer items-center font-suisse-intl text-button-sm text-primary-black outline-none"
          >
            <div className="mr-8 flex h-24 w-24 items-center justify-center rounded-full border border-solid border-black p-4">
              <span
                className={clsx('h-full w-full rounded-full bg-primary-magenta', {
                  block: field.value === option.value,
                  hidden: field.value !== option.value,
                })}
              />
            </div>
            <span className="radiobutton-text">{option.label}</span>
          </label>
        </div>
      ))}
      {meta.touched && meta.error && <FieldError>{meta.error}</FieldError>}
    </div>
  );
};

export default RadioButtonGroup;
